import {
  collection,
  query,
  orderBy,
  where,
  startAfter,
  getDocs,
  getCountFromServer,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
  QueryDocumentSnapshot,
  DocumentData,
  limit,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getDb, functions, getCallableName } from './firebase';
import type { QuoteRequest } from '../types';

const COLLECTION = 'quote_requests';

export interface ListQuoteRequestsParams {
  pageSize?: number;
  lastDoc?: QueryDocumentSnapshot<DocumentData> | null;
  statusFilter?: 'pending' | 'sent' | 'archived' | 'all';
  startDate?: string; // YYYY-MM-DD
  endDate?: string; // YYYY-MM-DD
}

export interface ListResult<T> {
  items: T[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}

function toIso(value: unknown): string {
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (typeof value === 'string') return value;
  return new Date().toISOString();
}

function toOptionalIso(value: unknown): string | undefined {
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (typeof value === 'string') return value;
  return undefined;
}

function toQuoteRequest(snap: QueryDocumentSnapshot<DocumentData>): QuoteRequest {
  const data = snap.data();
  return {
    id: snap.id,
    userId: data.userId,
    requestedAt: toIso(data.requestedAt),
    location: data.location ?? { district: '', county: '' },
    serviceId: data.serviceId ?? '',
    serviceName: data.serviceName ?? '',
    supplierIds: data.supplierIds ?? [],
    supplierNames: data.supplierNames ?? [],
    status: data.status ?? 'pending',
    emailSentAt: toOptionalIso(data.emailSentAt),
    searchKeywords: data.searchKeywords,
  };
}

/**
 * List quote requests with optional status filter and cursor-based pagination.
 * Date range filtering is performed in-memory because combining orderBy with
 * range where clauses on a different field requires a composite index.
 */
export async function listQuoteRequests({
  pageSize = 50,
  lastDoc: lastDocCursor = null,
  statusFilter = 'all',
  startDate,
  endDate,
}: ListQuoteRequestsParams = {}): Promise<ListResult<QuoteRequest>> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const constraints: any[] = [orderBy('requestedAt', 'desc')];

  if (statusFilter && statusFilter !== 'all') {
    constraints.push(where('status', '==', statusFilter));
  }

  if (lastDocCursor) {
    constraints.push(startAfter(lastDocCursor));
  }

  constraints.push(limit(pageSize + 1));

  const q = query(collection(getDb(), COLLECTION), ...constraints);
  const snapshot = await getDocs(q);

  const docs = snapshot.docs;
  const hasMore = docs.length > pageSize;
  const pageDocs = hasMore ? docs.slice(0, pageSize) : docs;

  let items = pageDocs.map(toQuoteRequest);

  // In-memory date range filtering
  if (startDate) {
    const start = new Date(startDate).getTime();
    items = items.filter(q => new Date(q.requestedAt).getTime() >= start);
  }
  if (endDate) {
    // Include the full end day
    const end = new Date(endDate).getTime() + 86_400_000 - 1;
    items = items.filter(q => new Date(q.requestedAt).getTime() <= end);
  }

  return {
    items,
    lastDoc: pageDocs.length > 0 ? pageDocs[pageDocs.length - 1] : null,
    hasMore,
  };
}

/**
 * Archive a quote request (set status to 'archived').
 */
export async function archiveQuoteRequest(id: string): Promise<void> {
  await updateDoc(doc(getDb(), COLLECTION, id), { status: 'archived' });
}

/**
 * Delete a quote request document.
 */
export async function deleteQuoteRequest(id: string): Promise<void> {
  await deleteDoc(doc(getDb(), COLLECTION, id));
}

/**
 * Trigger the resendQuoteEmails Cloud Function.
 */
export async function resendQuoteEmails(quoteId: string): Promise<void> {
  const callable = httpsCallable(functions, getCallableName('resendQuoteEmails'));
  await callable({ quoteId });
}

export async function countPendingQuotes(): Promise<number> {
  const snap = await getCountFromServer(
    query(collection(getDb(), COLLECTION), where('status', '==', 'pending'))
  );
  return snap.data().count;
}
